/**
 * useAdminAuthStore — single owner of the admin bearer token.
 *
 * The router guard, the transport layer's `authHeaders()`, the layout
 * login form, and the logout flow all read through this one reactive
 * owner. Admin auth is a domain concern, not a layout concern.
 *
 * Storage substrate
 * -----------------
 * The token lives in `sessionStorage`, not `localStorage`. Two reasons:
 *
 *   1. **Tab-scoped lifetime.** Admin auth is a higher-privilege surface
 *      than the speedtest user-session. Tying the admin token to the tab
 *      — cleared automatically when the tab closes, not shared across
 *      windows — is the right default.
 *   2. **No cross-tab leakage.** localStorage broadcasts to every tab on
 *      the origin; sessionStorage doesn't.
 *
 * httpOnly cookies would be stricter (no XSS read) but require server
 * cooperation we don't have today; sessionStorage is the achievable
 * improvement and the constant lives in one place so a future
 * server-cookie migration is a one-file change.
 */

import { defineStore, getActivePinia } from "pinia";
import { ref, computed } from "vue";
import type { Ref, ComputedRef } from "vue";

import { ADMIN_TOKEN_KEY } from "@src/api/client.ts";
import { resetStores } from "./plugins/resetAll.ts";

// Re-export the storage key so callers that already pull the auth store
// (the dedupe layer for tests + the future programmatic seed) can read
// the constant through one import. The literal still lives in api/client.ts
// per the api-as-leaf architecture rule (see api/client.ts:ADMIN_TOKEN_KEY).
export { ADMIN_TOKEN_KEY };

/** SSR-safe read of the persisted admin token. */
function readPersistedToken(): string | null {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

/**
 * Predicate: is this Pinia store id an admin-domain slab that
 * `clearToken()` resets on logout?
 *
 * Admin-domain state is everything gated behind the admin bearer token —
 * the `admin*` slabs (`adminSessions` / `adminResults` /
 * `adminDataSource`) plus the `dashboard*` filter slab that scopes the
 * admin-only dashboard views. Resetting these in lockstep with clearing
 * the token prevents stale rows from flashing on screen when the next
 * admin authenticates in the same tab — and re-seats the Live/Test
 * toggle on `prod` for a fresh login.
 *
 * Discovery by predicate (rather than a hand-maintained id literal)
 * means a future admin slab — say `adminUsers` — is reset automatically
 * the moment it adopts the `admin*` id convention; nothing here needs
 * editing. It also closes a latent bug the prior literal carried: it
 * listed `"dashboardFilter"` (singular) while the slab's real id is
 * `"dashboardFilters"` (plural), so the filter slab was silently never
 * reset. The prefix predicate matches the real id.
 *
 * The admin-auth store itself (`adminAuth`) is deliberately excluded —
 * `clearToken()` is the canonical un-set surface and must not reset
 * itself recursively.
 */
function isAdminDomainStoreId(id: string): boolean {
    if (id === "adminAuth") return false;
    return id.startsWith("admin") || id.startsWith("dashboard");
}

export const useAdminAuthStore = defineStore("adminAuth", () => {
    const token: Ref<string | null> = ref(readPersistedToken());

    const isAuthenticated: ComputedRef<boolean> = computed(
        () => token.value !== null && token.value.length > 0,
    );

    /** Persist the admin bearer token. Single public write path. */
    function setToken(next: string): void {
        token.value = next;
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(ADMIN_TOKEN_KEY, next);
        }
    }

    /**
     * Clear the in-memory token, the persisted copy, AND every admin-
     * domain Pinia slab in lockstep. The `resetAllPlugin` (registered
     * at app boot) holds the per-store initial-state snapshots; the
     * `resetStores(pinia, predicate)` call here discovers the admin-
     * domain slabs at logout time via `isAdminDomainStoreId` and replays
     * their snapshots so no cached row, no in-flight filter, and no
     * paged result survives the logout.
     */
    function clearToken(): void {
        token.value = null;
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        }
        const pinia = getActivePinia();
        if (pinia) {
            resetStores(pinia, isAdminDomainStoreId);
        }
    }

    return {
        token,
        isAuthenticated,
        setToken,
        clearToken,
    };
});
